"use client";

import { SupportedLanguage } from "../../../config/languages";
import { useClickOutside } from "../../../helpers/hooks/useClickOutside";
import { DropdownArrow } from "../../icons/DropdownArrow";
import { SORT_OPTIONS, type SortOrder } from "../constants";
import classNames from "classnames/bind";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./SortDropdown.module.css";

const cx = classNames.bind(styles);

interface SortDropdownProps {
  currentSort: SortOrder;
  locale: SupportedLanguage;
  sortUrls: {
    newest: string;
    oldest: string;
  };
  className?: string;
}

const SORT_LABELS = {
  de: {
    newest: "Neueste zuerst",
    oldest: "Älteste zuerst",
    sortBy: "Sortieren nach",
  },
  en: {
    newest: "Newest first",
    oldest: "Oldest first",
    sortBy: "Sort by",
  },
};

export const SortDropdown = ({
  currentSort,
  locale,
  sortUrls,
  className,
}: SortDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useClickOutside(dropdownRef, handleClose, isOpen);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          handleClose();
          buttonRef.current?.focus();
          break;
        case "Tab":
          // Let Tab work naturally but close dropdown when tabbing out
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  const currentLabel = SORT_LABELS[locale][currentSort];
  const sortByLabel = SORT_LABELS[locale].sortBy;

  return (
    <div className={cx("dropdown", className)} ref={dropdownRef}>
      <button
        ref={buttonRef}
        className={cx("trigger")}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${sortByLabel}: ${currentLabel}`}
        aria-controls="sort-menu"
        type="button"
      >
        <span className={cx("label")}>{currentLabel}</span>
        <DropdownArrow
          className={cx("icon", { open: isOpen })}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className={cx("menu")}
          role="menu"
          id="sort-menu"
          aria-labelledby={buttonRef.current?.id}
        >
          {SORT_OPTIONS.map((sortOption) => (
            <a
              key={sortOption}
              href={sortUrls[sortOption]}
              className={cx("option", { active: currentSort === sortOption })}
              role="menuitem"
              aria-current={currentSort === sortOption ? "true" : undefined}
              tabIndex={0}
            >
              {SORT_LABELS[locale][sortOption]}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
